import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../store';
import { contains, CommonOperations, ResourceOperations, IPermissions, LicenseType } from "../../store/permissions";
import { Checkbox } from 'office-ui-fabric-react';
import { Subscription, PPMFeatures, TimeTrackingGlobalSettings } from '../../store/Tenant';
import Collapsible from '../common/Collapsible';
import { notUndefined } from '../utils/common';
import InfoableComponent from '../common/InfoableComponent';
import Link from '../common/Link';

export type OperationArg = {
    operation?: CommonOperations;
    objectives?: ObjectivesAccessLevel;
};
export enum ObjectivesAccessLevel {
    Manage = 'manage',
    View = 'view',
}
type OwnProps = {
    isAdministrative: boolean;
    disabled?: (arg: OperationArg) => boolean | undefined;
    indeterminate?: (arg: OperationArg) => boolean | undefined;
    checked: (arg: OperationArg) => boolean;
    onChange: (arg: OperationArg, checked: boolean | undefined) => void;
    licenseType: LicenseType;
};
type StateProps = {
    subscription: Subscription;
    timeTrackingSettings: TimeTrackingGlobalSettings;
};

type Props = OwnProps & StateProps;

const CommonPermissionsImpl = (props: Props) => {
    const { subscription, disabled, indeterminate, checked, onChange, licenseType, timeTrackingSettings, isAdministrative } = props;
    const viewObjectivesOption = Subscription.contains(subscription, PPMFeatures.OKR)
        ? {
            label: licenseType === LicenseType.Viewer ? "View objectives" : "Collaborate on objectives",
            description: licenseType === LicenseType.Viewer ? <span>Grants permissions to view all Objectives on the tenant.</span>
                : <span>Grants permissions to create new top-level Objectives and create/import Sub-Objectives and Key Results. Click <Link target="_blank" href="https://help.ppm.express/people-management-and-permissions/user-permissions-in-ppm-express#section-1">here</Link> for more information.</span>,
            arg: { objectives: ObjectivesAccessLevel.View }
        }
        : undefined;
    const perms: { label: string, description: string | JSX.Element, arg: OperationArg }[] = licenseType === LicenseType.Regular
        ? [
            Subscription.contains(subscription, PPMFeatures.Prioritization) ? {
                label: "Manage prioritization",
                description: "Grants permissions to manage strategic alignments (users can edit the catalog of priorities and their importance).",
                arg: { operation: CommonOperations.PrioritizationManage }
            } : undefined,
            Subscription.contains(subscription, PPMFeatures.OKR) ? {
                label: "Manage objectives",
                description: "Grants permissions to create, edit, and delete all Objectives on the tenant and manage them.",
                arg: { objectives: ObjectivesAccessLevel.Manage }
            } : undefined,
            viewObjectivesOption,
            {
                label: "Manage financial details",
                description: <span>Grants permissions to review and edit the Budget and Benefits section and all fields of Cost type (OOB and Custom) and set the charge rates for resources. Click <Link target="_blank" href="https://help.ppm.express/people-management-and-permissions/user-permissions-in-ppm-express#section-1">here</Link> for more information.</span>,
                arg: { operation: CommonOperations.BudgetManage }
            },
            {
                label: "Manage configuration",
                description: <span>Grants permission to manage layouts and views, custom fields, public filters and subviews. Click <Link target="_blank" href="https://help.ppm.express/people-management-and-permissions/user-permissions-in-ppm-express#section-1">here</Link> for more information.</span>,
                arg: { operation: CommonOperations.ConfigurationManage }
            },
            {
                label: "Manage billing",
                description: "Grants access to manage billing details for card-based subscriptions, purchase a new subscription, and delete the account.",
                arg: { operation: CommonOperations.BillingManage }
            },
            {
                label: "Manage integrations",
                description: "Grants permission to create, refresh and manage all connections in PPM Express.",
                arg: { operation: CommonOperations.ConnectionManage }
            },
            Subscription.contains(subscription, PPMFeatures.Insights) ? {
                label: "PPM Insights",
                description: "Grants permission to visit the PPM Insights dashboard.",
                arg: { operation: CommonOperations.InsightsView }
            } : undefined,
            Subscription.containsAny(subscription, [PPMFeatures.TimeTracking, PPMFeatures.TimeTrackingEnterprise]) && timeTrackingSettings.newTimeTrackingEnabled
                ? {
                    label: "Manage Time Tracking",
                    description: "Grants permission for managing settings on the Time Tracking settings page and setting up rules for time reporting on the tenant level.",
                    arg: { operation: CommonOperations.TimeTrackingManage }
                }
                : undefined
        ].filter(notUndefined)
        : [
            viewObjectivesOption
        ].filter(notUndefined);

    if (!perms.length) {
        return null;
    }

    return <Collapsible
        expanded
        iconName="CheckList"
        title="General"
        classNames={{ head: isAdministrative ? "perm-header-readonly" : undefined }}>
        <div>
            {perms
                .map(_ => <InfoableComponent description={_.description}><Checkbox
                    key={_.label}
                    label={_.label}
                    disabled={disabled?.(_.arg)}
                    indeterminate={indeterminate?.(_.arg)}
                    checked={checked(_.arg)}
                    onChange={(e, c) => onChange(_.arg, c)}
                /></InfoableComponent>)}
        </div>
    </Collapsible>;
}

function mapStateToProps(state: ApplicationState): StateProps {
    return {
        subscription: state.tenant.subscription,
        timeTrackingSettings: state.tenant.timeTracking.globalSettings
    }
}
export const CommonPermissions = connect(mapStateToProps)(CommonPermissionsImpl);

export function isCommonPermissionChecked(arg: OperationArg, permissions: IPermissions) {
    if (arg.objectives === ObjectivesAccessLevel.Manage && arg.operation === undefined) {
        return contains(permissions.objective.global, ResourceOperations.Update);
    }
    if (arg.objectives === ObjectivesAccessLevel.View && arg.operation === undefined) {
        return contains(permissions.objective.global, ResourceOperations.Read);
    }

    return contains(permissions.common, arg.operation);
}